import AppsIcon from '@material-design-icons/svg/filled/apps.svg?react';
import { useEffect, useRef, useState } from 'react';
import { useS3Image } from '../../../hooks/useS3Image';
import { cn } from '../../../lib/utils';
import type { ReportingApp } from '../types';

type ReportingAppIconApp = Pick<
  ReportingApp,
  'dappIconThumbnailUrl' | 'dappIconUrl' | 'storeListing'
>;

type ReportingAppIconSize = 'sm' | 'md' | 'lg';

interface ReportingAppIconProps {
  app: ReportingAppIconApp;
  alt?: string;
  className?: string;
  loading?: 'eager' | 'lazy';
  size?: ReportingAppIconSize;
}

const sizeClasses: Record<ReportingAppIconSize, string> = {
  sm: 'h-8 w-8 rounded-md',
  md: 'h-10 w-10 rounded-lg',
  lg: 'h-14 w-14 rounded-xl',
};

const imageSizes: Record<ReportingAppIconSize, number> = {
  sm: 32,
  md: 40,
  lg: 56,
};

function normalizeSource(value: string | null | undefined) {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

export function ReportingAppIcon({
  app,
  alt = '',
  className,
  loading = 'lazy',
  size = 'sm',
}: ReportingAppIconProps) {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const seen = new Set<string>();
  const orderedSources = [
    app.dappIconThumbnailUrl,
    app.dappIconUrl,
    app.storeListing?.iconUrl,
  ];

  const sources = orderedSources.flatMap((source) => {
    const normalized = normalizeSource(source);
    if (!normalized || seen.has(normalized)) {
      return [];
    }

    seen.add(normalized);
    return [normalized];
  });
  const sourceKey = sources.join('\n');
  const [sourceIndex, setSourceIndex] = useState(0);
  const [trackedSourceKey, setTrackedSourceKey] = useState(sourceKey);
  const [shouldResolve, setShouldResolve] = useState(loading === 'eager');
  const source = shouldResolve ? (sources[sourceIndex] ?? null) : null;
  const { imageUrl, isLoading, error } = useS3Image(source);
  const imageSize = imageSizes[size];

  if (sourceKey !== trackedSourceKey) {
    setTrackedSourceKey(sourceKey);
    setSourceIndex(0);
  }

  useEffect(() => {
    if (loading === 'eager' || sources.length === 0) {
      setShouldResolve(true);
      return;
    }

    setShouldResolve(false);
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setShouldResolve(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldResolve(true);
          observer.disconnect();
        }
      },
      { rootMargin: '160px' },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [loading, sourceKey, sources.length]);

  useEffect(() => {
    if (!error || sourceIndex >= sources.length - 1) {
      return;
    }

    setSourceIndex((current) => Math.min(current + 1, sources.length - 1));
  }, [error, sourceIndex, sources.length]);

  return (
    <span
      ref={containerRef}
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden border border-neutral-800/70 bg-neutral-950/40',
        sizeClasses[size],
        className,
      )}
      aria-hidden={alt ? undefined : true}
    >
      {imageUrl && !error ? (
        <img
          src={imageUrl}
          alt={alt}
          className="h-full w-full object-cover"
          width={imageSize}
          height={imageSize}
          loading={loading}
          decoding="async"
        />
      ) : (
        <span
          className={cn(
            'flex h-full w-full items-center justify-center',
            isLoading && 'animate-pulse',
          )}
        >
          <AppsIcon
            className={cn(
              'fill-current text-neutral-600',
              size === 'lg' ? 'h-6 w-6' : 'h-4 w-4',
            )}
          />
        </span>
      )}
    </span>
  );
}
